import { useEffect } from 'react';

type DebouncedWindowEventOptions = {
  event: 'resize' | 'scroll';
  /** Milliseconds of event silence before `handler` fires. */
  delayMs: number;
  /** Called after the event stream has been idle for `delayMs`. */
  handler: () => void;
  /** Registers the listener with `{ passive: true }` to avoid blocking scroll. */
  passive?: boolean;
  /**
   * `resize`-only escape hatch. When `true`, the listener ignores events where
   * `document.documentElement.clientWidth` is unchanged from the previous
   * invocation.
   *
   * Why this exists — the mobile URL-bar edge case:
   *
   * On iOS Safari and Chrome for Android, the browser's URL bar (and tab bar
   * on iPad) shows when scrolling up and hides when scrolling down. Every
   * show/hide changes the visual viewport height, which the browser reports
   * as a `resize` event — even though the user is actively scrolling and
   * hasn't touched the window dimensions.
   *
   * For our virtual-scroll use case, width is what affects row height (card
   * aspect ratio scales with column width). Height-only resizes are noise
   * that we explicitly don't want to react to, because:
   *
   *   1. They fire mid-scroll — if we invalidated the row-height cache here,
   *      we'd stall smooth scroll-to-top row-by-row.
   *   2. They don't change any of the measurements we care about.
   *
   * Gating on `clientWidth` equality filters these out while still reacting
   * to real width changes (drag-resize, device rotation, split-screen).
   */
  widthChangeOnly?: boolean;
};

/**
 * Debounced window event listener. Subscribes to `resize` or `scroll` and
 * invokes `handler` only after `delayMs` of event silence, so continuous
 * event streams (drag-resize, smooth-scroll animations, momentum scroll)
 * don't thrash the handler.
 *
 * Use this when a handler should run once a user-initiated interaction has
 * settled, not on every event in the burst.
 */
const useDebouncedWindowEvent = ({
  event,
  delayMs,
  handler,
  passive,
  widthChangeOnly,
}: DebouncedWindowEventOptions) => {
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let lastWidth = widthChangeOnly
      ? (document?.documentElement?.clientWidth ?? window.innerWidth)
      : 0;
    const onEvent = () => {
      if (widthChangeOnly) {
        const nextWidth =
          document?.documentElement?.clientWidth ?? window.innerWidth;
        if (nextWidth === lastWidth) {
          return;
        }
        lastWidth = nextWidth;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(handler, delayMs);
    };
    const listenerOptions = passive ? { passive: true } : undefined;
    window.addEventListener(event, onEvent, listenerOptions);
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener(event, onEvent);
    };
  }, [event, delayMs, handler, passive, widthChangeOnly]);
};

export default useDebouncedWindowEvent;
